
import React, { useState, useEffect } from "react";
import { Box, Tabs, Tab, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import StaffBasicInfo from "./StaffBasicInfo";
import AdmAddress from "./AdmAddress";
import DocumentDetails from "./DocumentDetails";
import PreviousExperience from "./PreviousExperience";
import Courses from "./Courses";
import FamilyDetails from "./FamilyDetails";
import EducationalDetails from "./EducationalDetails";
import LanguagesKnown from "./LanguagesKnown";
import { ApiUrl } from "../../../ApiUrl";
import { useNavigate, useLocation } from "react-router-dom";
// import "./AdmStaffDetails.css"

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      style={{ display: value === index ? "block" : "none" }}
      {...other}
    >
      <Box sx={{ p: 3 }}>{children}</Box>
    </div>
  );
}

export default function BasicTabs() {
  const [value, setValue] = useState(0);
  const numberOfTabs = 8;
  const [requiredFieldsAlert, setRequiredFieldsAlert] = useState({
    isOpen: false,
    fields: [],
    actionText: "saving",
  });
  const [experienceData, setExperienceData] = useState([]); // data from PreviousExperience
  const [addressDetails, setAddressDetails] = useState(null);
  const [documentDetails, setDocumentDetails] = useState([]);
  const [relationDetails, setRelationDetails] = useState([]);
  const [educationData, setEducationData] = useState([]);
  const [courseDetails, setCourseDetails] = useState([]);
  const [languageData, setLanguageData] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const editEmployeeId =
    location.state?.employeeId ||
    sessionStorage.getItem("staff_edit_employee_id") ||
    null;
  const isEditMode = Boolean(editEmployeeId);

  useEffect(() => {
    if (location.state?.employeeId) {
      sessionStorage.setItem(
        "staff_edit_employee_id",
        String(location.state.employeeId)
      );
    }
  }, [location.state]);

  const hydrateUnifiedData = (data) => {
    const basic = data?.basicDetails || {};
    const address = data?.addressDetails || {};
    const documents = Array.isArray(data?.documentDetails)
      ? data.documentDetails
      : [];
    const family = Array.isArray(data?.familyDetails) ? data.familyDetails : [];
    const qualifications = Array.isArray(data?.qualificationDetails)
      ? data.qualificationDetails
      : [];
    const courses = Array.isArray(data?.courseDetails) ? data.courseDetails : [];
    const language = data?.languageDetails || {};
    const experience = Array.isArray(data?.experienceDetails)
      ? data.experienceDetails
      : [];

    if (Object.keys(basic).length) {
      const staffBasicPayload = {
        employee_id: basic.id || "",
        org_id: basic.org_id || 0,
        branch_id: basic.branch_id || 0,
        employee_code: basic.employee_code || "",
        title: basic.title || "",
        first_name: basic.first_name || "",
        middle_name: basic.middle_name || "",
        last_name: basic.last_name || "",
        dob: basic.dob || null,
        place_of_birth: basic.place_of_birth || "",
        marital_status: basic.marital_status || "",
        blood_group: basic.blood_group || null,
        nationality: basic.nationality || null,
        religion: basic.religion || null,
        gender: basic.gender || "",
        mother_tongue: basic.mother_tongue || null,
        employee_type: basic.employee_type_id || null,
        email: basic.email || "",
        office_email: basic.office_email || "",
        phone_number: basic.phone_number || "",
        emergency_contact_number: basic.emergency_contact_number || "",
        created_by: basic.created_by || 0,
      };
      sessionStorage.setItem(
        "staff_registration_basic",
        JSON.stringify(staffBasicPayload)
      );

      if (basic.profile) {
        sessionStorage.setItem("staff_profile_pic_base64", basic.profile);
        sessionStorage.setItem("staff_profile_pic_name", "profile.jpg");
        sessionStorage.setItem("staff_profile_pic_type", "image/jpeg");
      }
    }

    const addressPayload = {
      present_address: address.present_address || "",
      present_country: address.present_country || null,
      present_state: address.present_state || null,
      present_district: address.present_district || null,
      present_pincode: address.present_pincode || "",
      present_phone_number: address.present_phone_number || "",
      permanent_address: address.permanent_address || "",
      permanent_country: address.permanent_country || null,
      permanent_state: address.permanent_state || null,
      permanent_district: address.permanent_district || null,
      permanent_pincode: address.permanent_pincode || "",
      permanent_phone_number: address.permanent_phone_number || "",
    };

    sessionStorage.setItem(
      "staff_registration_address",
      JSON.stringify(addressPayload)
    );
    sessionStorage.setItem(
      "staff_registration_documents",
      JSON.stringify(documents)
    );
    sessionStorage.setItem("staff_registration_family", JSON.stringify(family));
    sessionStorage.setItem(
      "staff_registration_qualification",
      JSON.stringify(qualifications)
    );
    sessionStorage.setItem(
      "staff_registration_courses",
      JSON.stringify(courses)
    );
    sessionStorage.setItem(
      "staff_registration_language",
      JSON.stringify(language)
    );
    sessionStorage.setItem(
      "staff_registration_experience",
      JSON.stringify(experience)
    );

    setAddressDetails(addressPayload);
    setDocumentDetails(documents);
    setRelationDetails(family);
    setEducationData(qualifications);
    setCourseDetails(courses);
    setLanguageData(language);
    setExperienceData(experience);
  };

  // Initialize edit mode using one unified retrieve API.
  useEffect(() => {
    const initializeEditMode = async () => {
      if (!isEditMode) {
        return;
      }

      try {
        if (location.state?.unifiedData) {
          hydrateUnifiedData(location.state.unifiedData);
          return;
        }

        const response = await fetch(
          `${ApiUrl.apiurl}STAFF/registrationRetrieveUnified/${editEmployeeId}/`
        );
        const result = await response.json();
        if (response.ok && result.data) {
          hydrateUnifiedData(result.data);
        }
      } catch (error) {
        console.error("Error fetching employee data for edit:", error);
      }
    };

    initializeEditMode();
  }, [isEditMode, editEmployeeId, location.state]);

  const handleChange = (event, newValue) => setValue(newValue);
  const goToTab = (tabIndex) => setValue(tabIndex);

  const showRequiredFieldsAlert = (fields, actionText = "saving") => {
    setRequiredFieldsAlert({
      isOpen: true,
      fields,
      actionText,
    });
  };

  const closeRequiredFieldsAlert = () => {
    setRequiredFieldsAlert((prev) => ({ ...prev, isOpen: false }));
  };

  const getMissingBasicFields = (basic) => {
    const requiredBasicFields = [
      { key: "employee_code", label: "Employee Code" },
      { key: "first_name", label: "First Name" },
      { key: "phone_number", label: "Phone Number" },
      { key: "blood_group", label: "Blood Group" },
      { key: "nationality", label: "Nationality" },
      { key: "religion", label: "Religion" },
      { key: "mother_tongue", label: "Mother Tongue" },
      { key: "employee_type", label: "Employee Type" },
    ];

    return requiredBasicFields
      .filter((field) => {
        const value = basic?.[field.key];
        return (
          value === undefined ||
          value === null ||
          (typeof value === "string" && value.trim() === "")
        );
      })
      .map((field) => field.label);
  };

  const getMissingAddressFields = (address) => {
    const requiredAddressFields = [
      { key: "present_country", label: "Present Country" },
      { key: "present_state", label: "Present State" },
      { key: "present_district", label: "Present District" },
      { key: "present_pincode", label: "Present Pincode" },
    ];

    return requiredAddressFields
      .filter((field) => {
        const value = address?.[field.key];
        return (
          value === undefined ||
          value === null ||
          (typeof value === "string" && value.trim() === "")
        );
      })
      .map((field) => field.label);
  };

  const hasRequiredBasicDetails = (basic) => {
    return Boolean(
      basic?.employee_code &&
        basic?.first_name &&
        basic?.phone_number &&
        basic?.blood_group &&
        basic?.nationality &&
        basic?.religion &&
        basic?.mother_tongue &&
        basic?.employee_type
    );
  };

  const hasRequiredAddressDetails = (address) => {
    return Boolean(
      address?.present_country &&
        address?.present_state &&
        address?.present_district &&
        address?.present_pincode
    );
  };

  const handleArrowNext = () => {
    const basic = JSON.parse(
      sessionStorage.getItem("staff_registration_basic") || "{}"
    );
    const address = JSON.parse(
      sessionStorage.getItem("staff_registration_address") || "{}"
    );

    if (value === 0 && !hasRequiredBasicDetails(basic)) {
      showRequiredFieldsAlert(getMissingBasicFields(basic), "continuing");
      return;
    }

    if (value === 1 && !hasRequiredAddressDetails(address)) {
      showRequiredFieldsAlert(getMissingAddressFields(address), "continuing");
      return;
    }

    setValue((prev) => Math.min(prev + 1, numberOfTabs - 1));
  };

  const handleArrowPrevious = () => {
    setValue((prev) => Math.max(prev - 1, 0));
  };

  // const handleSave = async () => {
  //   const employeeId = localStorage.getItem("employeeId"); // Get employeeId from localStorage
  //   const createdBy = sessionStorage.getItem("userId"); // Get userId from sessionStorage

  //   const payload = {
  //     created_by: parseInt(createdBy), // Ensure integer type if API expects int
  //     experience_details: experienceData.map((item) => ({
  //       experience_id: "0",
  //       previous_company_worked: item.organization,
  //       month_year_from: item.monthYearFrom,
  //       month_year_to: item.monthYearTo,
  //       reason_for_leaving: item.reasonForLeaving,
  //       experience_letter_provided: item.experienceLetterProvided ? "T" : "F",
  //     })),
  //   };

  //   try {
  //     const response = await fetch(
  //       `${ApiUrl.apiurl}STAFF/registrationEXPERIENCECreateUpdate/${employeeId}/`, // Dynamic employeeId
  //       {
  //         method: "PUT",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify(payload),
  //       }
  //     );

  //     if (!response.ok) {
  //       throw new Error("Network response was not ok");
  //     }

  //     const result = await response.json();
  //     console.log("API Success:", result);
  //     alert("Data saved successfully!");
  //   } catch (error) {
  //     console.error("API Error:", error);
  //     alert("Error saving data!");
  //   }
  // };

  const handleSave = async () => {
    try {
      const basic = JSON.parse(
        sessionStorage.getItem("staff_registration_basic") || "{}"
      );
      const address = JSON.parse(
        sessionStorage.getItem("staff_registration_address") || "{}"
      );
      const documents = JSON.parse(
        sessionStorage.getItem("staff_registration_documents") || "[]"
      );
      const family = JSON.parse(
        sessionStorage.getItem("staff_registration_family") || "[]"
      );
      const qualification = JSON.parse(
        sessionStorage.getItem("staff_registration_qualification") || "[]"
      );
      const courses = JSON.parse(
        sessionStorage.getItem("staff_registration_courses") || "[]"
      );
      const language = JSON.parse(
        sessionStorage.getItem("staff_registration_language") || "{}"
      );

      const normalizedLanguage =
        language && typeof language === "object"
          ? {
              ...language,
              language_code:
                language.language_code ||
                (Array.isArray(language.language_codes)
                  ? language.language_codes[0]
                  : undefined),
            }
          : {};

      let experience = JSON.parse(
        sessionStorage.getItem("staff_registration_experience") || "[]"
      );

      if (!experience.length && Array.isArray(experienceData) && experienceData.length) {
        experience = experienceData.map((item) => ({
          previous_company_worked: item.organization,
          month_year_from: item.monthYearFrom,
          month_year_to: item.monthYearTo,
          reason_for_leaving: item.reasonForLeaving,
          experience_letter_provided: !!item.experienceLetterProvided,
        }));
      }

      if (!hasRequiredBasicDetails(basic)) {
        showRequiredFieldsAlert(
          getMissingBasicFields(basic),
          isEditMode ? "updating" : "saving"
        );
        return;
      }

      if (!hasRequiredAddressDetails(address)) {
        showRequiredFieldsAlert(
          getMissingAddressFields(address),
          isEditMode ? "updating" : "saving"
        );
        return;
      }

      const orgId = parseInt(localStorage.getItem("orgId") || "0", 10);
      const branchId = parseInt(localStorage.getItem("branchId") || "0", 10);

      const sanitizedBasic = {
        employee_code: basic.employee_code || "",
        title: basic.title || "",
        first_name: basic.first_name || "",
        middle_name: basic.middle_name || "",
        last_name: basic.last_name || "",
        dob: basic.dob || null,
        place_of_birth: basic.place_of_birth || "",
        marital_status: basic.marital_status || "",
        blood_group: basic.blood_group || null,
        nationality: basic.nationality || null,
        religion: basic.religion || null,
        gender: basic.gender || "",
        mother_tongue: basic.mother_tongue || null,
        employee_type: basic.employee_type || null,
        email: basic.email || "",
        office_email: basic.office_email || "",
        phone_number: basic.phone_number || "",
        emergency_contact_number: basic.emergency_contact_number || "",
        created_by: Number(basic.created_by || sessionStorage.getItem("userId") || 0),
        org_id: Number(basic.org_id || orgId || 0),
        branch_id: Number(basic.branch_id || branchId || 0),
      };

      if (basic.employee_id) {
        sanitizedBasic.employee_id = Number(basic.employee_id);
      }

      const sanitizedExperience = Array.isArray(experience)
        ? experience
            .filter(
              (item) =>
                item?.previous_company_worked &&
                item?.month_year_from &&
                item?.month_year_to &&
                item?.reason_for_leaving
            )
            .map((item) => ({
              previous_company_worked: item.previous_company_worked,
              month_year_from: item.month_year_from,
              month_year_to: item.month_year_to,
              reason_for_leaving: item.reason_for_leaving,
              experience_letter_provided: Boolean(item.experience_letter_provided),
            }))
        : [];

      const formPayload = new FormData();
      formPayload.append("staffBasicDetails", JSON.stringify(sanitizedBasic));
      formPayload.append("addressDetails", JSON.stringify(address));
      formPayload.append("documentDetails", JSON.stringify(documents));
      formPayload.append("familyDetails", JSON.stringify(family));
      formPayload.append("qualificationDetails", JSON.stringify(qualification));
      formPayload.append("courseDetails", JSON.stringify(courses));
      formPayload.append("languageDetails", JSON.stringify(normalizedLanguage));
      formPayload.append("experienceDetails", JSON.stringify(sanitizedExperience));

      const base64Data = sessionStorage.getItem("staff_profile_pic_base64");
      const fileName = sessionStorage.getItem("staff_profile_pic_name");
      const fileType = sessionStorage.getItem("staff_profile_pic_type");

      if (base64Data && fileName && fileType) {
        try {
          const res = await fetch(base64Data);
          const blob = await res.blob();
          formPayload.append("upload_image", new File([blob], fileName, { type: fileType }));
        } catch (error) {
          console.warn("Error attaching staff profile image:", error);
        }
      }

      let url = `${ApiUrl.apiurl}STAFF/registrationSaveUnified/`;
      let method = "POST";

      if (isEditMode) {
        url = `${ApiUrl.apiurl}STAFF/registrationUpdateUnified/${editEmployeeId}/`;
        method = "PUT";
      }

      const response = await fetch(url, {
        method: method,
        body: formPayload,
      });

      const result = await response.json();
      if (!response.ok || result.message !== "success") {
        const errorMessage =
          result.error ||
          result.message ||
          `Failed to ${isEditMode ? "update" : "save"} staff registration`;

        if (/required|missing|mandatory/i.test(errorMessage)) {
          showRequiredFieldsAlert([errorMessage], isEditMode ? "updating" : "saving");
          return;
        }

        throw new Error(errorMessage);
      }

      alert(`Staff registration ${isEditMode ? "updated" : "saved"} successfully!`);

      handleClear();

      setTimeout(() => {
        navigate("/admin/employee-Search");
      }, 500);
    } catch (error) {
      console.error("Unified save error:", error);
      alert(error.message || "Error saving staff registration.");
    }
  };

  const handleClear = () => {
  [
    "staff_edit_employee_id",
    "staff_registration_basic",
    "staff_registration_address",
    "staff_registration_documents",
    "staff_registration_family",
    "staff_registration_qualification",
    "staff_registration_courses",
    "staff_registration_language",
    "staff_registration_experience",
    "staff_profile_pic_base64",
    "staff_profile_pic_name",
    "staff_profile_pic_type",
    "tempFormData",
    "tempFrontCover",
  ].forEach((key) => sessionStorage.removeItem(key));

  setExperienceData([]);
  setAddressDetails(null);
  setDocumentDetails([]);
  setRelationDetails([]);
  setEducationData([]);
  setCourseDetails([]);
  setLanguageData([]);
  setValue(0);

  window.location.reload();
};

  return (
    // <Box sx={{ width: "100%" }}>
    //   <div className="row">
    //     <div className="col-12 d-flex justify-content-around">
    //       <button
    //         className="btn btn-primary me-2"
    //         style={{ width: "150px" }}
    //         onClick={handleSave}
    //         disabled={!saveEnabled}
    //       >
    //         Save
    //       </button>
    //       <button className="btn btn-secondary me-2" style={{ width: "150px" }}>
    //         Clear
    //       </button>
    //       <button className="btn btn-danger me-2" style={{ width: "150px" }}>
    //         Close
    //       </button>
    //     </div>
    //   </div>

    //   {/* <Tabs value={value} onChange={handleChange} variant="standard">
    //     <Tab label="Staff Basic Info" {...a11yProps(0)} />
    //     <Tab label="Address" {...a11yProps(1)} />
    //     <Tab label="Documents" {...a11yProps(2)} />
    //     <Tab label="Family" {...a11yProps(3)} />
    //     <Tab label="Educational" {...a11yProps(4)} />
    //     <Tab label="Courses" {...a11yProps(5)} />
    //     <Tab label="Languages Known" {...a11yProps(6)} />
    //     <Tab label="Previous Experience" {...a11yProps(7)} />
    //   </Tabs> */}
    //   <Box sx={{ width: "100%", overflowX: "auto" }}>
    //     <Tabs
    //       value={value}
    //       onChange={handleChange}
    //       variant="scrollable"
    //       scrollButtons="auto"
    //       aria-label="staff tabs"
    //       sx={{
    //         whiteSpace: "nowrap", // prevent wrapping
    //         "& .MuiTabs-flexContainer": {
    //           flexWrap: "nowrap",
    //         },
    //       }}
    //     >
    //       <Tab label="Staff Basic Info" sx={{ minWidth: 150 }} />
    //       <Tab label="Address" sx={{ minWidth: 150 }} />
    //       <Tab label="Documents" sx={{ minWidth: 150 }} />
    //       <Tab label="Family" sx={{ minWidth: 150 }} />
    //       <Tab label="Educational" sx={{ minWidth: 150 }} />
    //       <Tab label="Courses" sx={{ minWidth: 150 }} />
    //       <Tab label="Languages Known" sx={{ minWidth: 150 }} />
    //       <Tab label="Previous Experience" sx={{ minWidth: 150 }} />
    //     </Tabs>
    //   </Box>

    //   <CustomTabPanel value={value} index={0}>
    //     <StaffBasicInfo
    //       goToTab={goToTab}
    //       setAddressDetails={setAddressDetails}
    //     />
    //   </CustomTabPanel>
    //   <CustomTabPanel value={value} index={1}>
    //     <AdmAddress
    //       goToTab={goToTab}
    //       addressDetails={addressDetails}
    //       setDocumentDetailsInParent={setDocumentDetails}
    //     />
    //   </CustomTabPanel>
    //   <CustomTabPanel value={value} index={2}>
    //     <DocumentDetails
    //       goToTab={goToTab}
    //       documentDetails={documentDetails}
    //       setRelationDetailsInParent={setRelationDetails}
    //     />
    //   </CustomTabPanel>

    //   <CustomTabPanel value={value} index={3}>
    //     <FamilyDetails
    //       goToTab={goToTab}
    //       relationDetails={relationDetails}
    //       setEducationDetailsInParent={setEducationData}
    //     />
    //   </CustomTabPanel>
    //   <CustomTabPanel value={value} index={4}>
    //     <EducationalDetails
    //       goToTab={goToTab}
    //       educationData={educationData}
    //       setCourseDetailsInParent={setCourseDetails}
    //     />
    //   </CustomTabPanel>
    //   <CustomTabPanel value={value} index={5}>
    //     <Courses
    //       goToTab={goToTab}
    //       prefilledCourses={courseDetails}
    //       setLanguageDataInParent={setLanguageData}
    //     />
    //   </CustomTabPanel>
    //   <CustomTabPanel value={value} index={6}>
    //     <LanguagesKnown
    //       goToTab={goToTab}
    //       languageData={languageData}
    //       setExperienceDataInParent={setExperienceData}
    //     />
    //   </CustomTabPanel>
    //   <CustomTabPanel value={value} index={7}>
    //     {/* <PreviousExperience
    //       goToTab={goToTab}
    //       setExperienceData={setExperienceData}
    //       experienceData={experienceData}
    //     /> */}
    //     <PreviousExperience
    //       goToTab={goToTab}
    //       setExperienceData={(data) => {
    //         setExperienceData(data);
    //         enableSaveButton(); //  Updated function name
    //       }}
    //       experienceData={experienceData}
    //     />
    //   </CustomTabPanel>
    // </Box>

    <Box sx={{ width: "100%" }}>
      <div className="row mb-3 mt-3 mx-0">
        <h4
          style={{ textAlign: "center", marginTop: "5px", fontWeight: "700" }}
        >
          STAFF REGISTRATION
        </h4>
        <div className="col-12 d-flex flex-wrap gap-4">
          <button
            className="btn btn-primary me-5"
            style={{ width: "150px" }}
            onClick={handleSave}
            disabled={isEditMode}
          >
            Save
          </button>
          <button
            className="btn btn-primary me-5"
            style={{ width: "150px" }}
            onClick={handleSave}
            disabled={!isEditMode}
          >
            Update
          </button>
          <button
            className="btn btn-secondary me-5"
            style={{ width: "150px" }}
            onClick={handleClear}
          >
            Clear
          </button>
          <button
            className="btn btn-danger me-5"
            style={{ width: "150px" }}
            onClick={() => navigate("/admin/dashboard")}
          >
            Close
          </button>
        </div>
      </div>

      {/* Tabs section */}
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="staff tabs"
          sx={{
            whiteSpace: "nowrap",
            "& .MuiTabs-flexContainer": { flexWrap: "nowrap" },
          }}
        >
          <Tab label="Staff Basic Info" sx={{ minWidth: 150 }} />
          <Tab label="Address" sx={{ minWidth: 150 }} />
          <Tab label="Documents" sx={{ minWidth: 150 }} />
          <Tab label="Family" sx={{ minWidth: 150 }} />
          <Tab label="Educational" sx={{ minWidth: 150 }} />
          <Tab label="Courses" sx={{ minWidth: 150 }} />
          <Tab label="Languages Known" sx={{ minWidth: 150 }} />
          <Tab label="Previous Experience" sx={{ minWidth: 150 }} />
        </Tabs>
      </Box>

      {/* Panels */}
      <CustomTabPanel value={value} index={0}>
        <StaffBasicInfo
          goToTab={goToTab}
          setAddressDetails={setAddressDetails}
        />
      </CustomTabPanel>

      <CustomTabPanel value={value} index={1}>
        <AdmAddress
          goToTab={goToTab}
          addressDetails={addressDetails}
          setDocumentDetailsInParent={setDocumentDetails}
        />
      </CustomTabPanel>

      <CustomTabPanel value={value} index={2}>
        <DocumentDetails
          goToTab={goToTab}
          documentDetails={documentDetails}
          setRelationDetailsInParent={setRelationDetails}
        />
      </CustomTabPanel>

      <CustomTabPanel value={value} index={3}>
        <FamilyDetails
          goToTab={goToTab}
          relationDetails={relationDetails}
          setEducationDetailsInParent={setEducationData}
        />
      </CustomTabPanel>

      <CustomTabPanel value={value} index={4}>
        <EducationalDetails
          goToTab={goToTab}
          educationData={educationData}
          setCourseDetailsInParent={setCourseDetails}
        />
      </CustomTabPanel>

      <CustomTabPanel value={value} index={5}>
        <Courses
          goToTab={goToTab}
          prefilledCourses={courseDetails}
          setLanguageDataInParent={setLanguageData}
        />
      </CustomTabPanel>

      <CustomTabPanel value={value} index={6}>
        <LanguagesKnown
          goToTab={goToTab}
          languageData={languageData}
          setExperienceDataInParent={setExperienceData}
        />
      </CustomTabPanel>

      <CustomTabPanel value={value} index={7}>
        <PreviousExperience
          goToTab={goToTab}
          setExperienceData={(data) => {
            setExperienceData(data);
          }}
          experienceData={experienceData}
        />
      </CustomTabPanel>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <IconButton
          onClick={handleArrowPrevious}
          disabled={value === 0}
          sx={{ mr: 1 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <IconButton
          onClick={handleArrowNext}
          disabled={value === numberOfTabs - 1}
          sx={{ ml: 1 }}
        >
          <ArrowForwardIcon />
        </IconButton>
      </Box>

      {requiredFieldsAlert.isOpen && (
        <div
          className="modal show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div
              className="modal-content"
              style={{ borderRadius: "8px", overflow: "hidden" }}
            >
              <div
                className="modal-header"
                style={{
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  borderBottom: "none",
                }}
              >
                <h5 className="modal-title" style={{ fontWeight: 700 }}>
                  Missing Required Fields
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  aria-label="Close"
                  onClick={closeRequiredFieldsAlert}
                />
              </div>

              <div className="modal-body" style={{ padding: "14px 18px 10px" }}>
                <p
                  className="text-center mb-3"
                  style={{ color: "#dc3545", fontWeight: 700 }}
                >
                  Please fill in the following required fields before {requiredFieldsAlert.actionText}:
                </p>
                <ul className="mb-0 ps-3">
                  {(requiredFieldsAlert.fields || []).length > 0 ? (
                    requiredFieldsAlert.fields.map((field) => (
                      <li key={field}>{field}</li>
                    ))
                  ) : (
                    <li>One or more required fields are missing.</li>
                  )}
                </ul>
              </div>

              <div className="modal-footer" style={{ borderTop: "none", paddingTop: 0 }}>
                <button
                  type="button"
                  className="btn"
                  style={{
                    backgroundColor: "#dc3545",
                    color: "#fff",
                    minWidth: "110px",
                  }}
                  onClick={closeRequiredFieldsAlert}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
}

